// Supabase owns authentication; only its access JWT is passed to SpacetimeDB.
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { claimsOf } from './stdbLogin';

export interface SupabaseOptions {
  /** Supabase project reference (the subdomain), not a URL or dashboard display name. */
  projectId: string;
  /** Public publishable/anon key only. NEVER use a service-role or secret key in the browser. */
  publishableKey: string;
  /** Offer "Play as guest": Supabase anonymous sign-in (enable Anonymous Sign-Ins in the project first). */
  guests?: boolean;
}
let client: SupabaseClient | null = null;
let token: string | null = null;
export const authConfigured = (o: SupabaseOptions): boolean => !!(o.projectId.trim() && o.publishableKey);
/** Construct only a hosted Supabase URL; reject URLs, paths and other hostnames. */
export function supabaseUrl(projectId: string): string {
  const id = projectId.trim();
  if (!/^[a-z0-9]+$/.test(id)) throw new Error('Enter your Supabase project ID (lowercase letters and numbers), not a URL.');
  return `https://${id}.supabase.co`;
}

type AuthStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** Whether a stored Supabase session belongs to an anonymous (guest) user. */
function anonymousSession(value: string): boolean {
  try { return (JSON.parse(value) as { user?: { is_anonymous?: unknown } })?.user?.is_anonymous === true; } catch { return false; }
}

/**
 * Email links open new tabs. Share the temporary PKCE verifier, and guest (anonymous) sessions: a guest has no
 * password to sign in again with, so it outlives the tab. Account sessions stay in the tab and win over the guest.
 */
export function authStorage(tab: AuthStorage, shared: AuthStorage): AuthStorage {
  const verifier = (key: string) => key.endsWith('-code-verifier');
  return {
    getItem: key => verifier(key) ? shared.getItem(key) ?? tab.getItem(key) : tab.getItem(key) ?? shared.getItem(key),
    setItem: (key, value) => {
      if (verifier(key) || anonymousSession(value)) { shared.setItem(key, value); tab.removeItem(key); }
      else tab.setItem(key, value);
    },
    removeItem: key => {
      // Signing out of an account in this tab leaves the browser's guest alone.
      if (verifier(key) || tab.getItem(key) === null) shared.removeItem(key);
      tab.removeItem(key);
    },
  };
}

const redirectUri = () => `${location.origin}${location.pathname}`;
function auth(): SupabaseClient {
  if (!client) throw new Error('Supabase sign-in is not configured.');
  return client;
}

/** Restore/refresh the tab's session and handle GitHub/email-confirmation PKCE callbacks. */
export async function completeSignIn(o: SupabaseOptions): Promise<string | null> {
  if (!authConfigured(o)) return null;
  const query = new URLSearchParams(location.search);
  const callback = query.has('code') || query.has('error');
  const callbackError = query.get('error_description') || query.get('error');
  const url = supabaseUrl(o.projectId);
  client = createClient(url, o.publishableKey, { auth: {
    flowType: 'pkce', persistSession: true, autoRefreshToken: true, detectSessionInUrl: false,
    storage: authStorage(sessionStorage, localStorage), storageKey: `ashenheart:supabase:${new URL(url).host}`,
  } });
  client.auth.onAuthStateChange((_event, session) => { token = session?.access_token ?? null; });
  try {
    if (callbackError) throw new Error(callbackError);
    const code = query.get('code');
    const { data, error } = code
      ? await client.auth.exchangeCodeForSession(code)
      : await client.auth.getSession();
    if (error?.code === 'pkce_code_verifier_not_found') {
      throw new Error('This sign-in link was opened outside the browser that started it, or its sign-in data was cleared. If you just confirmed your email, try signing in with your email and password. For GitHub, start sign-in again here.');
    }
    if (error) throw error;
    token = data.session?.access_token ?? null;
    if (callback && !token) throw new Error('That sign-in has expired; please try again.');
    return callback ? token : null;
  } finally {
    if (callback) history.replaceState(null, '', redirectUri());
  }
}

export function signedInToken(): string | null { return token; }

/** Whether the signed-in user is a guest (anonymous; the token's `is_anonymous` claim). */
export function signedInAsGuest(): boolean {
  return !!token && claimsOf(token).is_anonymous === true;
}

/** A new guest: a Supabase anonymous user, kept in this browser (see authStorage) until signed out. */
export async function signInGuest(): Promise<string> {
  const { data, error } = await auth().auth.signInAnonymously();
  if (error) throw error;
  if (!data.session) throw new Error('Guest play is unavailable.');
  token = data.session.access_token;
  return token;
}

/** getSession refreshes an expiring JWT before a new world connection. */
export async function accessToken(): Promise<string> {
  const { data, error } = await auth().auth.getSession();
  if (error) throw error;
  if (!data.session) throw new Error('Please sign in again.');
  token = data.session.access_token;
  return token;
}

export async function signIn(email: string, password: string): Promise<string> {
  const { data, error } = await auth().auth.signInWithPassword({ email: email.trim(), password });
  if (error) throw error;
  token = data.session.access_token;
  return token;
}

/** Null means email confirmation is required before the account can play. */
export async function signUp(email: string, password: string): Promise<string | null> {
  const { data, error } = await auth().auth.signUp({ email: email.trim(), password,
    options: { emailRedirectTo: redirectUri() } });
  if (error) throw error;
  token = data.session?.access_token ?? null;
  return token;
}

export async function signInGithub(): Promise<void> {
  const { data, error } = await auth().auth.signInWithOAuth({ provider: 'github', options: { redirectTo: redirectUri(), skipBrowserRedirect: true } });
  if (error) throw error;
  if (!data.url) throw new Error('GitHub sign-in is unavailable.');
  location.assign(data.url);
}

export async function signOut(): Promise<void> {
  const { error } = await auth().auth.signOut({ scope: 'local' });
  if (error) throw error;
  token = null;
}
